import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { APP_LOCALE } from "../../../config/locale";
import {
  ApiError,
  createScheduleTask,
  deleteScheduleTask,
  getScheduleTasks,
  getScheduleTaskStatuses,
  notifyScheduleTaskDelay,
  updateScheduleTask,
  type ScheduleTask,
  type ScheduleTaskStatus,
} from "../../../cronograma/cronogramaApi";
import { getBillingMilestones, type BillingMilestone } from "../../../marcos/marcosApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";
import { useLookupOptions } from "../../../lookups/useLookupOptions";
import { EntidadeExecutanteField } from "./EntidadeExecutanteField";
import { Modal } from "../../../components/Modal";
import { ListToolbar } from "../../../components/ListToolbar";
import { DetailsToggle } from "../../../components/DetailsToggle";

interface WorkScheduleTabProps {
  workId: number;
}

const CONCLUIDA_CODE = "concluida";

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

type AlertKind = "atrasada" | "em_breve" | null;

const computeAlert = (task: ScheduleTask, today: Date): AlertKind => {
  if (!task.plannedEndDate) return null;
  if (task.statusCode === CONCLUIDA_CODE) return null;
  const end = new Date(task.plannedEndDate);
  if (Number.isNaN(end.getTime())) return null;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  if (endDay.getTime() < startOfToday.getTime()) return "atrasada";
  const inSevenDays = new Date(startOfToday);
  inSevenDays.setDate(inSevenDays.getDate() + 7);
  if (endDay.getTime() <= inSevenDays.getTime()) return "em_breve";
  return null;
};

// Shape of the editable form (shared by the create and edit modal variants).
interface TaskForm {
  name: string;
  responsible: string;
  plannedStartDate: string;
  plannedEndDate: string;
  statusId: string;
  billingMilestoneId: string;
  subcontractorId: string;
  notes: string;
}

const emptyForm = (statusId: string): TaskForm => ({
  name: "",
  responsible: "",
  plannedStartDate: "",
  plannedEndDate: "",
  statusId,
  billingMilestoneId: "",
  subcontractorId: "",
  notes: "",
});

const formFromTask = (task: ScheduleTask): TaskForm => ({
  name: task.name ?? "",
  responsible: task.responsible ?? "",
  plannedStartDate: toInputDate(task.plannedStartDate),
  plannedEndDate: toInputDate(task.plannedEndDate),
  statusId: String(task.statusId),
  billingMilestoneId: task.billingMilestoneId !== null && task.billingMilestoneId !== undefined ? String(task.billingMilestoneId) : "",
  subcontractorId: task.subcontractorId !== null && task.subcontractorId !== undefined ? String(task.subcontractorId) : "",
  notes: task.notes ?? "",
});

export function WorkScheduleTab({ workId }: WorkScheduleTabProps) {
  const [tasks, setTasks] = useState<ScheduleTask[]>([]);
  const [filter, setFilter] = useState("");
  const [statuses, setStatuses] = useState<ScheduleTaskStatus[]>([]);
  const [milestones, setMilestones] = useState<BillingMilestone[]>([]);
  const workersLookup = useLookupOptions("workers");
  const subcontractorsLookup = useLookupOptions("subcontractors");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [statusSavingId, setStatusSavingId] = useState<number | null>(null);
  const [notifyingId, setNotifyingId] = useState<number | null>(null);

  const [form, setForm] = useState<TaskForm>(emptyForm(""));
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [editingTask, setEditingTask] = useState<ScheduleTask | null>(null);
  const [draft, setDraft] = useState<TaskForm | null>(null);

  // Which list row has its read-only "Ver detalhes" sub-row expanded.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const toggleExpanded = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  const loadTasks = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      setTasks(await getScheduleTasks(workId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar o cronograma.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statusList, milestoneList] = await Promise.all([
          getScheduleTaskStatuses(),
          getBillingMilestones(workId),
        ]);
        if (cancelled) return;
        setStatuses(statusList);
        setMilestones(milestoneList);
      } catch {
        // Non-blocking: tasks still render; selects just have fewer options.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workId]);

  const defaultStatusId = useMemo(() => {
    const porIniciar = statuses.find((s) => s.code === "por_iniciar");
    return porIniciar?.id ?? statuses[0]?.id ?? null;
  }, [statuses]);

  useEffect(() => {
    if (form.statusId === "" && defaultStatusId !== null) {
      setForm((prev) => ({ ...prev, statusId: String(defaultStatusId) }));
    }
  }, [defaultStatusId, form.statusId]);

  // O lookup de trabalhadores já vem só com os activos; o Responsável guarda o nome, não o id.
  const activeWorkerNames = useMemo(
    () => Array.from(new Set(workersLookup.options.map((option) => option.label))),
    [workersLookup.options],
  );

  const subcontractorOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "— Sem subempreiteiro —" },
      ...subcontractorsLookup.options.map((option) => ({
        ...option,
        value: String(option.value),
      })),
    ],
    [subcontractorsLookup.options],
  );

  const today = useMemo(() => new Date(), []);

  const alerts = useMemo(() => {
    let atrasadas = 0;
    let emBreve = 0;
    for (const task of tasks) {
      const kind = computeAlert(task, today);
      if (kind === "atrasada") atrasadas += 1;
      else if (kind === "em_breve") emBreve += 1;
    }
    return { atrasadas, emBreve };
  }, [tasks, today]);

  const normalizedFilter = filter.trim().toLowerCase();
  const shown = useMemo(() => {
    if (normalizedFilter === "") return tasks;
    return tasks.filter((task) =>
      [task.name ?? "", task.responsible ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(normalizedFilter),
    );
  }, [tasks, normalizedFilter]);

  const resetForm = () => {
    setForm(emptyForm(defaultStatusId !== null ? String(defaultStatusId) : ""));
  };

  // Create entry-point: clear the form and open the (create) modal.
  const openCreate = () => {
    resetForm();
    setError(null);
    setIsCreateOpen(true);
  };

  // Close whichever variant of the form modal is open (create or edit).
  const closeForm = () => {
    setIsCreateOpen(false);
    setEditingTask(null);
    setDraft(null);
  };

  const handleAdd = async () => {
    if (isSaving) return;
    if (!form.name.trim()) {
      setError("O nome da tarefa é obrigatório.");
      return;
    }
    const statusId = form.statusId !== "" ? Number(form.statusId) : defaultStatusId;
    if (statusId === null) {
      setError("Não foi possível determinar o estado da tarefa.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createScheduleTask({
        workId,
        name: form.name.trim(),
        plannedStartDate: form.plannedStartDate !== "" ? form.plannedStartDate : null,
        plannedEndDate: form.plannedEndDate !== "" ? form.plannedEndDate : null,
        statusId,
        responsible: form.responsible.trim() !== "" ? form.responsible.trim() : null,
        billingMilestoneId: form.billingMilestoneId !== "" ? Number(form.billingMilestoneId) : null,
        subcontractorId: form.subcontractorId !== "" ? Number(form.subcontractorId) : null,
        sortOrder: tasks.length,
        notes: form.notes.trim() !== "" ? form.notes.trim() : null,
      });
      if (!created.statusCode) {
        await loadTasks(true);
      } else {
        setTasks((prev) => [...prev, created]);
      }
      resetForm();
      setIsCreateOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível adicionar a tarefa.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (task: ScheduleTask, newStatusId: number) => {
    if (statusSavingId !== null) return;
    setStatusSavingId(task.id);
    setError(null);
    try {
      const updated = await updateScheduleTask(task.id, {
        id: task.id,
        workId: task.workId,
        name: task.name,
        plannedStartDate: task.plannedStartDate ?? null,
        plannedEndDate: task.plannedEndDate ?? null,
        statusId: newStatusId,
        responsible: task.responsible ?? null,
        billingMilestoneId: task.billingMilestoneId ?? null,
        subcontractorId: task.subcontractorId ?? null,
        sortOrder: task.sortOrder,
        notes: task.notes ?? null,
      });
      if (!updated.statusCode) {
        await loadTasks(true);
      } else {
        setTasks((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar o estado.");
      loadTasks(true);
    } finally {
      setStatusSavingId(null);
    }
  };

  const handleDelete = async (taskId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar esta tarefa?")) return;
    setDeletingId(taskId);
    setError(null);
    try {
      await deleteScheduleTask(taskId);
      setTasks((prev) => prev.filter((item) => item.id !== taskId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setTasks((prev) => prev.filter((item) => item.id !== taskId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar a tarefa.");
        loadTasks(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleNotifyDelay = async (task: ScheduleTask) => {
    if (notifyingId !== null) return;
    if (!task.subcontractorEmail) return;
    const who = task.subcontractorName ?? "o subempreiteiro";
    const from = formatDate(task.previousEndDate);
    const to = formatDate(task.plannedEndDate);
    if (!window.confirm(`Notificar ${who} (${task.subcontractorEmail}) da alteração de ${from} → ${to}?`)) return;
    setNotifyingId(task.id);
    setError(null);
    setSuccess(null);
    try {
      await notifyScheduleTaskDelay(task.id);
      await loadTasks(true);
      setSuccess("Subempreiteiro notificado.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível notificar o subempreiteiro.");
    } finally {
      setNotifyingId(null);
    }
  };

  const openEdit = (task: ScheduleTask) => {
    setError(null);
    setIsCreateOpen(false);
    setEditingTask(task);
    setDraft(formFromTask(task));
  };

  const saveEdit = async () => {
    if (!editingTask || !draft) return;
    if (!draft.name.trim()) {
      setError("O nome da tarefa é obrigatório.");
      return;
    }
    setError(null);
    try {
      const updated = await updateScheduleTask(editingTask.id, {
        id: editingTask.id,
        workId,
        name: draft.name.trim(),
        plannedStartDate: draft.plannedStartDate !== "" ? draft.plannedStartDate : null,
        plannedEndDate: draft.plannedEndDate !== "" ? draft.plannedEndDate : null,
        statusId: draft.statusId !== "" ? Number(draft.statusId) : editingTask.statusId,
        responsible: draft.responsible.trim() !== "" ? draft.responsible.trim() : null,
        billingMilestoneId: draft.billingMilestoneId !== "" ? Number(draft.billingMilestoneId) : null,
        subcontractorId: draft.subcontractorId !== "" ? Number(draft.subcontractorId) : null,
        sortOrder: editingTask.sortOrder,
        notes: draft.notes.trim() !== "" ? draft.notes.trim() : null,
      });
      if (!updated.statusCode) {
        await loadTasks(true);
      } else {
        setTasks((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      }
      setEditingTask(null);
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar a tarefa.");
    }
  };

  const renderAlertBadge = (task: ScheduleTask) => {
    const kind = computeAlert(task, today);
    if (kind === "atrasada") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-red-600 bg-red-500/10" style={{ borderRadius: "var(--radius)" }}>
          Atrasada
        </span>
      );
    }
    if (kind === "em_breve") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-yellow-700 bg-yellow-500/10" style={{ borderRadius: "var(--radius)" }}>
          Termina em breve
        </span>
      );
    }
    return <span className="text-secondary-text">—</span>;
  };

  const updateForm = (patch: Partial<TaskForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const updateDraft = (patch: Partial<TaskForm>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  // Shared form body for both the create and edit modals (same fields),
  // parameterized by which form state it reads/writes.
  const renderFormBody = (
    source: TaskForm,
    update: (patch: Partial<TaskForm>) => void,
  ) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
        Nome *
        <input placeholder="Nome da tarefa" value={source.name} maxLength={300} onChange={(e) => update({ name: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
      </label>
      <div className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
        Responsável
        {/* O responsável guarda texto livre (há tarefas antigas com nomes fora da equipa); o
            seletor sugere os trabalhadores e a primeira opção mantém a escrita à mão. */}
        <EntidadeExecutanteField
          value={source.responsible}
          onChange={(valor) => update({ responsible: valor })}
          options={activeWorkerNames}
          inputClassName="px-3 py-2 border border-border rounded bg-background text-foreground"
          placeholder="Responsável"
          maxLength={200}
          ariaLabel="Responsável"
        />
      </div>
      <SearchableSelect<string>
        className="md:col-span-2"
        label="Subempreiteiro"
        labelClassName="text-sm text-secondary-text"
        labelStyle={{}}
        value={source.subcontractorId}
        onChange={(next) => update({ subcontractorId: next ?? "" })}
        options={subcontractorOptions}
        loading={subcontractorsLookup.loading}
        allowClear={false}
        searchPlaceholder="Procurar subempreiteiro…"
      />
      <label className="flex flex-col gap-1 text-sm text-secondary-text">
        Início
        <input type="date" value={source.plannedStartDate} onChange={(e) => update({ plannedStartDate: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
      </label>
      <label className="flex flex-col gap-1 text-sm text-secondary-text">
        Fim
        <input type="date" value={source.plannedEndDate} onChange={(e) => update({ plannedEndDate: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
      </label>
      <label className="flex flex-col gap-1 text-sm text-secondary-text">
        Estado
        <select value={source.statusId} onChange={(e) => update({ statusId: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground">
          {statuses.map((status) => (
            <option key={status.id} value={status.id}>{status.name}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-secondary-text">
        Marco
        <select value={source.billingMilestoneId} onChange={(e) => update({ billingMilestoneId: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground">
          <option value="">—</option>
          {milestones.map((milestone) => (
            <option key={milestone.id} value={milestone.id}>{milestone.description}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
        Notas
        <textarea value={source.notes} maxLength={1000} rows={3} onChange={(e) => update({ notes: e.target.value })} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
      </label>
    </div>
  );

  return (
    <div className="space-y-6">
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar tarefas…"
        summary={<span className="text-secondary-text text-sm">{shown.length} tarefa(s)</span>}
      >
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Nova tarefa
        </button>
      </ListToolbar>

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      {success && <div className="bg-green-500/10 text-green-700 px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{success}</div>}

      {(alerts.atrasadas > 0 || alerts.emBreve > 0) && (
        <div className="text-sm text-secondary-text">
          ⚠️{" "}
          {alerts.atrasadas > 0 && (
            <span className="text-red-600 font-medium">{alerts.atrasadas} {alerts.atrasadas === 1 ? "atrasada" : "atrasadas"}</span>
          )}
          {alerts.atrasadas > 0 && alerts.emBreve > 0 && " · "}
          {alerts.emBreve > 0 && (
            <span className="text-yellow-700 font-medium">{alerts.emBreve} a terminar em breve</span>
          )}
        </div>
      )}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="w-full" style={{ minWidth: "880px" }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 56 }}></th>
              <th className="px-4 py-3">Tarefa</th>
              <th className="px-4 py-3">Responsável</th>
              <th className="px-4 py-3">Início</th>
              <th className="px-4 py-3">Fim</th>
              <th className="px-4 py-3" style={{ width: "160px" }}>Estado</th>
              <th className="px-4 py-3">Marco</th>
              <th className="px-4 py-3">Alerta</th>
              <th className="px-4 py-3" style={{ width: "220px" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-secondary-text">{filter !== "" ? "Sem resultados para o filtro." : "Sem tarefas no cronograma. Use \"+ Nova tarefa\" para adicionar a primeira."}</td></tr>
            ) : shown.map((task) => (
              <Fragment key={task.id}>
                <tr className="border-b border-border last:border-b-0 align-top">
                  <td className="px-4 py-3">
                    <DetailsToggle open={expandedId === task.id} onToggle={() => toggleExpanded(task.id)} />
                  </td>
                  <td className="px-4 py-3 whitespace-normal break-words">{task.name || "—"}</td>
                  <td className="px-4 py-3 break-words">{task.responsible || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(task.plannedStartDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span>{formatDate(task.plannedEndDate)}</span>
                      {task.deadlineChangePending && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-yellow-700 bg-yellow-500/10" style={{ borderRadius: "var(--radius)" }}>
                          Prazo alterado
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={task.statusId}
                      disabled={statusSavingId === task.id}
                      onChange={(event) => handleStatusChange(task, Number(event.target.value))}
                      className="px-2 py-1 border border-border rounded bg-background text-foreground disabled:opacity-50"
                    >
                      {statuses.map((status) => (
                        <option key={status.id} value={status.id}>{status.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 break-words">
                    {task.billingMilestoneDescription ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20" style={{ borderRadius: "var(--radius)" }}>
                        {task.billingMilestoneDescription}
                      </span>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{renderAlertBadge(task)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap items-center">
                      <button className="text-primary underline" type="button" onClick={() => openEdit(task)}>Editar</button>
                      <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === task.id} onClick={() => handleDelete(task.id)}>{deletingId === task.id ? "A eliminar…" : "Eliminar"}</button>
                      {task.deadlineChangePending && (
                        task.subcontractorEmail ? (
                          <button
                            className="text-primary underline disabled:opacity-50 disabled:cursor-not-allowed"
                            type="button"
                            disabled={notifyingId === task.id}
                            onClick={() => handleNotifyDelay(task)}
                          >
                            {notifyingId === task.id ? "A notificar…" : "Notificar subempreiteiro"}
                          </button>
                        ) : (
                          <span className="text-secondary-text text-xs">Subempreiteiro sem email</span>
                        )
                      )}
                    </div>
                  </td>
                </tr>
                {expandedId === task.id && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={9} className="px-4 py-3 bg-muted">
                      <div className="text-sm"><span className="text-secondary-text">Subempreiteiro: </span>{task.subcontractorName || "—"}{task.subcontractorEmail ? ` (${task.subcontractorEmail})` : ""}</div>
                      {task.deadlineChangePending && (
                        <div className="text-sm"><span className="text-secondary-text">Prazo alterado: </span>{formatDate(task.previousEndDate)} → {formatDate(task.plannedEndDate)}{task.subcontractorEmail ? " · subempreiteiro por notificar" : ""}</div>
                      )}
                      <div className="text-sm"><span className="text-secondary-text">Notas: </span>{task.notes || "—"}</div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {(isCreateOpen || (editingTask && draft)) && (
        <Modal
          title={editingTask ? "Editar tarefa" : "Nova tarefa"}
          onClose={closeForm}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button type="button" onClick={closeForm} className="px-4 py-2 min-h-[44px] border border-border" style={{ borderRadius: "var(--radius-button)" }}>Cancelar</button>
              <button
                type="button"
                disabled={!editingTask && isSaving}
                onClick={editingTask ? saveEdit : handleAdd}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {!editingTask && isSaving ? "A adicionar…" : "Guardar"}
              </button>
            </>
          }
        >
          {editingTask && draft
            ? renderFormBody(draft, updateDraft)
            : renderFormBody(form, updateForm)}
        </Modal>
      )}
    </div>
  );
}
